import { Check } from '@mui/icons-material';
import { type InputHTMLAttributes, useRef } from 'react';
import type { FieldValues, Path, RegisterOptions, UseFormRegister, UseFormWatch } from 'react-hook-form';

interface CheckboxProps<T extends FieldValues> extends InputHTMLAttributes<HTMLInputElement> {
  register: UseFormRegister<T>;
  watch: UseFormWatch<T>;
  registerName: Path<T>;
  label?: string;
  registerProps?: RegisterOptions<T, Path<T>>;
  isDisabled?: boolean;
  error?: FieldValues['errors'];
  isChecked?: boolean;
  inputKey?: string;
  labelClassName?: string;
  containerClassName?: string;
}

export const Checkbox = <T extends FieldValues>({
  label,
  register,
  registerName,
  registerProps,
  isDisabled,
  error,
  onChange,
  watch,
  inputKey,
  isChecked,
  labelClassName,
  containerClassName,
  ...rest
}: CheckboxProps<T>) => {
  const { ref, onChange: onChangeFromRegister, ...others } = register(registerName, registerProps);

  const checked = watch(registerName) ?? false;
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className={`flex items-center gap-2 ${containerClassName}`}>
      <span className="relative w-5 h-5 border border-gray-300 rounded-[6px] checked:bg-secondary checked:border-transparent focus:outline-none focus:ring-2 focus:bg-secondary focus:ring-offset-2 focus:ring-offset-gray-100 grid content-center">
        <Check
          className={`text-primary absolute w-3 h-3 transition-opacity duration-300 ease-in-out left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2
					 ${isDisabled ? (isChecked || checked ? 'opacity-50' : 'opcaity-0') : ''}
					 ${(isChecked ?? checked) ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => {
            inputRef?.current?.click();
          }}
        />
        <input
          {...rest}
          type="checkbox"
          id={registerName}
          className="appearance-none h-5 w-5 border border-gray-300 rounded-[6px] checked:bg-secondary checked:border-transparent before:transition-all"
          {...others}
          onChange={(e) => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }

            onChangeFromRegister?.(e);
            onChange?.(e);
          }}
          ref={(e) => {
            inputRef.current = e;
            ref(e);
          }}
          checked={isChecked ?? checked}
          key={inputKey}
        />
      </span>
      {label && (
        <label htmlFor={registerName} className={`ml-2 text-sm ${labelClassName}`}>
          {label}
        </label>
      )}
      {error && <span className="text-xs text-red-500">{error.message}</span>}
    </div>
  );
};
